import { useQuery, queryOptions, QueryKey } from '@tanstack/react-query';

import { QueryConfig } from '@/types/react-query';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

/**
 * 持久化配置选项
 */
export interface PersistenceOptions {
  /** 是否启用持久化到 localStorage */
  persist?: boolean;
  /** 持久化键名，如果不提供则使用 queryKey 的字符串形式 */
  persistKey?: string;
  /** 数据新鲜时间（毫秒），默认 5 分钟 */
  staleTime?: number;
  /** 垃圾回收时间（毫秒），默认 2 小时 */
  gcTime?: number;
  /** 是否在窗口聚焦时重新获取数据 */
  refetchOnWindowFocus?: boolean;
  /** 是否在网络重连时重新获取数据 */
  refetchOnReconnect?: boolean;
}

/**
 * 精简说明（持久化 / 新鲜时间 / 垃圾回收 / 是否发起请求）
 * - 持久化（persist）：只负责把内存缓存写入存储并在重启时恢复；不决定是否"新鲜"。
 * - 新鲜时间（staleTime）：在此时间内视为"新鲜"，挂载时一般不发请求；超过后为"陈旧（stale）"，按策略重取。
 * - 垃圾回收（gcTime）：仅影响"内存中的移除"，与已写入持久化的条目独立。
 * - maxAge：全局配置在 persistOptions 中；超过时恢复会丢弃持久化数据。
 * - 是否发起请求：由 staleTime + 重取选项决定（如 refetchOnMount、refetchOnWindowFocus、refetchOnReconnect、enabled）。
 *   即使存在 localStorage 恢复的数据，也可能在"陈旧"时触发请求；"新鲜"时通常不请求。
 */

/**
 * A factory function to create a reusable `useQuery` hook and `queryOptions` for API calls.
 *
 * @param fetcher The asynchronous function that fetches the data. It receives an object of parameters.
 * @param getQueryKey A function that generates a unique query key based on the parameters.
 * @returns An object containing `getQueryOptions` for use with `queryClient` and `useQueryHook` for use in components.
 *
 * ---
 *
 * ## Example Usage
 *
 * ### 1. Define your API fetcher function:
 *
 * ```ts
 * // src/features/discussions/api/get-discussion.ts
 * import { get } from '@/libs/http';
 *
 * export const getDiscussion = ({ discussionId }: { discussionId: string }) => {
 *   return get(`/discussions/${discussionId}`);
 * };
 * ```
 *
 * ### 2. Create the custom query hook and options using the factory:
 *
 * ```ts
 * // src/features/discussions/api/get-discussion.ts
 * import { createApiQuery } from '@/hooks/use-api-query';
 *
 * const { getQueryOptions, useQueryHook } = createApiQuery(
 *   getDiscussion,
 *   (params) => ['discussions', params.discussionId],
 * );
 *
 * export const getDiscussionQueryOptions = getQueryOptions;
 * export const useDiscussion = useQueryHook;
 * ```
 *
 * ### 3. Use the hook in your component:
 *
 * ```tsx
 * // src/components/discussion.tsx
 * import { useDiscussion } from '@/features/discussions/api/get-discussion';
 *
 * const Discussion = ({ discussionId }) => {
 *   const { data, isLoading } = useDiscussion({ discussionId });
 *
 *   if (isLoading) return 'Loading...';
 *
 *   return <div>{data.title}</div>;
 * };
 * ```
 *
 * ### 4. Use the options for prefetching:
 *
 * ```ts
 * // Somewhere in your app, e.g., on hover
 * import { queryClient } from '@/libs/react-query';
 * import { getDiscussionQueryOptions } from '@/features/discussions/api/get-discussion';
 *
 * queryClient.prefetchQuery(getDiscussionQueryOptions({ discussionId: 'some-id' }));
 * ```
 */
export function createApiQuery<TParams extends AnyRecord, TData>(
  fetcher: (params: TParams) => Promise<TData>,
  getQueryKey: (params: TParams) => QueryKey,
) {
  const getQueryOptions = (params: TParams) => {
    return queryOptions({
      queryKey: getQueryKey(params),
      queryFn: () => fetcher(params),
    });
  };

  type UseQueryOptions = TParams & {
    queryConfig?: QueryConfig<() => ReturnType<typeof getQueryOptions>>;
  };

  const useQueryHook = (options: UseQueryOptions = {} as UseQueryOptions) => {
    const { queryConfig, ...params } = options;
    return useQuery({
      ...getQueryOptions(params as TParams),
      ...queryConfig,
    });
  };

  /**
   * 不缓存版本的 useQueryHook
   * - gcTime: 0 - 立即清除缓存
   * - staleTime: 0 - 数据立即过期
   * - refetchOnMount: 'always' - 每次挂载都重新请求
   * - refetchOnWindowFocus: true - 窗口聚焦时重新请求
   * - refetchOnReconnect: true - 网络重连时重新请求
   */
  const useNoCacheQueryHook = (options: UseQueryOptions = {} as UseQueryOptions) => {
    const { queryConfig, ...params } = options;
    return useQuery({
      ...getQueryOptions(params as TParams),
      gcTime: 0,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      ...queryConfig, // 允许外部覆盖
    });
  };

  return { getQueryOptions, useQueryHook, useNoCacheQueryHook };
}

/**
 * 带持久化功能的 API 查询工厂函数
 * 
 * @param fetcher 异步数据获取函数
 * @param getQueryKey 生成查询键的函数
 * @param persistenceOptions 持久化配置选项
 * @returns 包含 getQueryOptions 和 useQueryHook 的对象
 * 
 * ---
 * 
 * ## 使用示例
 * 
 * ### 1. 创建带持久化的查询 Hook
 * 
 * ```ts
 * // src/features/user/api/get-user-profile.ts
 * import { createApiQueryWithPersistence } from '@/hooks/use-api-query';
 * import { get } from '@/libs/http';
 * 
 * export const getUserProfile = ({ userId }: { userId: string }) => {
 *   return get(`/users/${userId}/profile`);
 * };
 * 
 * const { getQueryOptions, useQueryHook } = createApiQueryWithPersistence(
 *   getUserProfile,
 *   (params) => ['user-profile', params.userId],
 *   {
 *     persist: true,
 *     persistKey: 'user-profile-cache',
 *     staleTime: 10 * 60 * 1000, // 10分钟
 *     gcTime: 2 * 60 * 60 * 1000, // 2小时
 *   }
 * );
 * 
 * export const getUserProfileQueryOptions = getQueryOptions;
 * export const useUserProfile = useQueryHook;
 * ```
 * 
 * ### 2. 在组件中使用
 * 
 * ```tsx
 * // src/components/user-profile.tsx
 * import { useUserProfile } from '@/features/user/api/get-user-profile';
 * 
 * const UserProfile = ({ userId }: { userId: string }) => {
 *   const { data, isLoading, error, isStale } = useUserProfile({ userId });
 * 
 *   if (isLoading) return <div>加载中...</div>;
 *   if (error) return <div>错误: {error.message}</div>;
 * 
 *   return (
 *     <div>
 *       <h1>{data.name}</h1>
 *       <p>{data.email}</p>
 *       {isStale && <p>数据可能已过期，正在后台更新...</p>}
 *     </div>
 *   );
 * };
 * ```
 * 
 * ### 3. 手动清除持久化缓存
 * 
 * ```ts
 * import { queryClient } from '@/libs/react-query';
 * import { getUserProfileQueryOptions } from '@/features/user/api/get-user-profile';
 * 
 * // 清除特定用户的缓存
 * queryClient.removeQueries({
 *   queryKey: ['user-profile', 'user123']
 * });
 * 
 * // 清除所有用户资料缓存
 * queryClient.removeQueries({
 *   queryKey: ['user-profile']
 * });
 * ```
 */
export function createApiQueryWithPersistence<TParams extends AnyRecord, TData>(
  fetcher: (params: TParams) => Promise<TData>,
  getQueryKey: (params: TParams) => QueryKey,
  persistenceOptions: PersistenceOptions = {}
) {
  const {
    persist = true,
    persistKey,
    staleTime = 5 * 60 * 1000, // 5分钟
    gcTime = 2 * 60 * 60 * 1000, // 2小时
    refetchOnWindowFocus = false,
    refetchOnReconnect = true,
  } = persistenceOptions;

  const getQueryOptions = (params: TParams) => {
    const queryKey = getQueryKey(params);

    return queryOptions({
      queryKey,
      queryFn: () => fetcher(params),
      staleTime,
      gcTime,
      refetchOnWindowFocus,
      refetchOnReconnect,
      // 启用持久化的关键配置
      ...(persist && {
        // 使用自定义的持久化键名，或者使用 queryKey 的字符串形式
        meta: {
          persist: true,
          persistKey: persistKey || `persist-${JSON.stringify(queryKey)}`,
        },
      }),
    });
  };

  type UseQueryOptions = TParams & {
    queryConfig?: QueryConfig<() => ReturnType<typeof getQueryOptions>>;
  };

  const useQueryHook = (options: UseQueryOptions = {} as UseQueryOptions) => {
    const { queryConfig, ...params } = options;
    return useQuery({
      ...getQueryOptions(params as TParams),
      ...queryConfig,
    });
  };

  return { getQueryOptions, useQueryHook };
}

/**
 * 创建简单的持久化查询 Hook（不需要参数）
 * 
 * @param fetcher 异步数据获取函数
 * @param queryKey 查询键
 * @param persistenceOptions 持久化配置选项
 * @returns useQuery Hook
 * 
 * ## 使用示例
 * 
 * ```ts
 * // src/features/settings/api/get-app-settings.ts
 * import { createSimplePersistentQuery } from '@/hooks/use-api-query';
 * import { get } from '@/libs/http';
 * 
 * export const getAppSettings = () => {
 *   return get('/settings/app');
 * };
 * 
 * export const useAppSettings = createSimplePersistentQuery(
 *   getAppSettings,
 *   ['app-settings'],
 *   {
 *     persist: true,
 *     persistKey: 'app-settings-cache',
 *     staleTime: 30 * 60 * 1000, // 30分钟
 *   }
 * );
 * ```
 * 
 * ### 运行时配置示例
 * 
 * ```tsx
 * // 在组件中根据条件启用/禁用查询
 * const { data, isLoading } = useAppSettings({ 
 *   enabled: isUserLoggedIn 
 * });
 * ```
 */
export function createSimplePersistentQuery<TData>(
  fetcher: () => Promise<TData>,
  queryKey: QueryKey,
  persistenceOptions: PersistenceOptions = {}
) {
  const { getQueryOptions } = createApiQueryWithPersistence(
    () => fetcher(),
    () => queryKey,
    persistenceOptions
  );

  return (config?: QueryConfig<any>) => {
    return useQuery({
      ...getQueryOptions({}),
      ...config,
    });
  };
}
